import sessionStore from 'client/Session/SessionStore';

/**
 * Placeholder shown in place of a restricted question's title wherever it would otherwise appear in
 * a question list / navigator / header (the title-redaction rule in `references/ui-patterns.md`).
 * User-facing "restrict" vocabulary (decided 2026-06-08); "lock" stays the internal/backend term.
 */
export const RESTRICTED_QUESTION_TITLE = 'Restricted Question';

/**
 * Generic, school-less restriction copy shown to students. Students are never told which school
 * restricts the content (that drilldown is admin-only, per `references/ui-patterns.md`).
 */
export const STUDENT_RESTRICTION_MESSAGE = 'Access restricted by your admin.';

/**
 * Heading + body for the student locked-question block panel (admin-locked-assessments, Part 4).
 * Student-friendly, school-less framing — attributes the lock to "your teacher", never a school.
 */
export const STUDENT_LOCKED_QUESTION_HEADING = 'This question is locked';
export const STUDENT_LOCKED_QUESTION_MESSAGE =
  'Your teacher has locked this question for now. Keep going with the rest of your assignment.';

/** Minimal structural shape: a question that may carry the server-enforced content-lock flag. */
interface RestrictableQuestion {
  isContentLocked?: () => boolean | undefined;
}

/**
 * The single decision seam for the student locked-content surfaces (admin-locked-assessments,
 * Part 4 — issue #17843).
 *
 * Returns whether a question's content is restricted, reading the server-enforced
 * `is_content_locked` virtual attribute (set by the backend ContentLock enforcement,
 * `mandark/lib/mandark/schools/content_lock/question_enforcement.ex`, which also redacts the
 * content fields). This synchronous signal IS the flag gate: the backend only sets it (and only
 * nulls the content) under `:global_content_restriction_enforcement`, so no flag → no signal → no
 * block. We deliberately do NOT add an async FE feature-flag query on top — the backend nulls the
 * content, so a not-yet-resolved async gate would let the empty question render (and crash) before
 * the block could swap in. Defensive: returns `false` when the signal is absent.
 *
 * TODO(part-1 backend): the in-assignment scope is meant to be the single classroom school — the
 * one permissive exception in `references/access-rules.md` — surfaced via a viewer + assignment
 * scoped Ash GraphQL field on the question. Until that lands this consumes the legacy
 * union-of-affiliated-schools enforcement signal: correct for single-school students; the
 * multi-school in-assignment refinement is a backend follow-up. Swapping the read below is the only
 * change required — callers are unaffected.
 */
export function isQuestionContentLocked(
  question: RestrictableQuestion | null | undefined
): boolean {
  return Boolean(question?.isContentLocked?.());
}

/**
 * Whether this question is restricted for the current student viewer (role + content-lock signal).
 * The Part 4 surfaces are student-only — a teacher previewing the same restricted question gets the
 * teacher block-and-message owned by Part 3. The feature-flag gate is implicit: the
 * `is_content_locked` signal is itself backend-flag-gated (only emitted under
 * `:global_content_restriction_enforcement`), so reacting to it engages the block synchronously on
 * the first render without a separate async FE flag query.
 */
export function isQuestionRestrictedForStudent(
  question: RestrictableQuestion | null | undefined
): boolean {
  return sessionStore.isStudent() && isQuestionContentLocked(question);
}
